import * as yup from 'yup'
import { Controller, useForm } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'
import { validationMessages } from 'constants/validation'

import { FormControl, FormControlLabel, FormHelperText, Radio, RadioGroup, Stack, TextField } from '@mui/material'
import { Section } from 'components/controls/section'

import { MatchingCondition } from "../types"
import { useCallback } from 'react'

const {
  Required
} = validationMessages

const formSchema = yup.object().shape({
	target: yup.string().equals(['kojin', 'sisetu']).required(Required),
	searchWords: yup.string().required(Required),
	senmon: yup.string(),
})

type useSisetuMatchingConditionFormProps = {
	condition?: MatchingCondition
	onSubmit: (condition: MatchingCondition) => void
}
export const useSisetuMatchingConditionForm = ({
	condition,
	onSubmit
}: useSisetuMatchingConditionFormProps) => {
	const form = useForm({
		defaultValues: condition ? {
			target: condition.target,
			searchWords: condition.searchWords,
			senmon: condition.senmon
		} : {
			target: 'kojin',
			searchWords: '',
			senmon: ''
		},
		mode: 'onChange',
		resolver: yupResolver(formSchema)
	})
	return {
		...form,
    submit: form.handleSubmit(input => onSubmit({
			target: input.target,
			searchWords: input.searchWords,
			senmon: input.senmon ?? ''
		}))
	}
}

export const SisetuMatchingConditionForm = ({
	control,
	watch,
	formState: { errors }
}: ReturnType<typeof useSisetuMatchingConditionForm>) => {
	const target = watch("target")
	const onKeyDown = useCallback(
		(e: React.KeyboardEvent<HTMLInputElement>) => {
			if (e.key === 'Tab') {
				e.preventDefault()
				
				const { value, selectionStart, selectionEnd } = e.target as unknown as {
					value: string,
					selectionStart: number,
					selectionEnd: number
				}
				const el: HTMLInputElement = e.target as HTMLInputElement
				el.value = value.substring(0, selectionStart) + '\t' + value.substring(selectionEnd)
				el.selectionStart = selectionStart + 1
				el.selectionEnd = selectionStart + 1
				//console.log(el, { value, selectionEnd, selectionStart })
			}
		},
		[]
	)
	return (
		<Stack direction="column">
			<Section label="対象" labelProps={{ py: 2 }} disablePaddingY>
				<Controller
					control={control}
					name="target"
					render={({ field }) => (
						<FormControl>
							<RadioGroup
								{...field}
								row
							>
								<FormControlLabel value="kojin" control={<Radio/>} label="医師" />
								<FormControlLabel value="sisetu" control={<Radio/>} label="施設" />
							</RadioGroup>
						</FormControl>
					)}
				/>
			</Section>

			<Section label={target === 'kojin' ? "医師名" : "施設名"} labelProps={{ py: 2 }}>
				<Controller
					control={control}
					name="searchWords"
					render={({ field }) => (
						<>
							<TextField
								{...field}
								variant="outlined"
								fullWidth
								multiline
								rows={18}
								onKeyDown={onKeyDown}
								error={!!errors.searchWords}
								helperText={errors.searchWords ? errors.searchWords.message : ''}
							/>
							<FormHelperText>
								{target === 'kojin' ? (
									"名寄せ対象の医師名[<Tab>所属施設名※省略可]の形式で列挙してください"
								) : target === 'sisetu' ? (
									"名寄せ対象の施設名[<Tab>都道府県※省略可]の形式で列挙してください"
								) : ''}
							</FormHelperText>
						</>
					)}
				/>
			</Section>

			{target === 'kojin' && (
				<Section label="専門" labelProps={{ paddingY: 2 }}>
					<Controller
						control={control}
						name="senmon"
						render={({ field }) => (
							<>
								<TextField
									{...field}
									variant="outlined"
									size="small"
									fullWidth
									error={!!errors.senmon}
									helperText={errors.senmon ? errors.senmon.message : ''}
								/>
								<FormHelperText>
									専門医資格名や診療科などの情報をカンマ区切りで指定してください
								</FormHelperText>
							</>
						)}
					/>
				</Section>
			)}
		</Stack>
	)
}